/**
 * Cosa stanno facendo le automazioni su questo lead, detto a parole.
 *
 * Fino al 13/09/2026 la scheda non lo mostrava affatto: un agente poteva
 * parlare col contatto per ore e chi apriva il lead non lo sapeva. Qui si vede se
 * l'assistente sta seguendo la conversazione, se si aspetta una risposta, e
 * com'e' finita l'ultima volta.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bot, Clock, CheckCircle2, PauseCircle, AlertCircle, Workflow, Square } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';
import { useUserData } from '../../lib/auth';
import { descriviAutomazione, type AutomazioneSulLead, type Tono } from './automazioniLead';

interface Props {
  clientId: string;
  organizationId: string;
}

const ICONA: Record<Tono, { Icon: typeof Bot; colore: string }> = {
  in_corso: { Icon: Bot, colore: 'text-[#0F7B6C]' },
  attesa: { Icon: Clock, colore: 'text-[#D9A404]' },
  concluso: { Icon: CheckCircle2, colore: 'text-[#0F7B6C]' },
  fermo: { Icon: PauseCircle, colore: 'text-[#9B9A97]' },
  errore: { Icon: AlertCircle, colore: 'text-[#EB5757]' },
};

export function LeadAutomazioniInCorso({ clientId, organizationId }: Props) {
  const [righe, setRighe] = useState<AutomazioneSulLead[]>([]);
  const [caricato, setCaricato] = useState(false);
  const [fermando, setFermando] = useState<string | null>(null);
  const { confirm, toast } = useApp();
  const { userProfile } = useUserData();

  const carica = useCallback(async () => {
    // Le attive per prime, poi le ultime concluse: bastano poche righe.
    const { data } = await supabase
      .from('automation_workflow_entities')
      .select(
        'id, status, entered_at, completed_at, exit_reason, trigger_data, ' +
          'workflow:automation_workflows(name), ' +
          'step:automation_workflow_steps!automation_workflow_entities_current_step_id_fkey(id, name, step_type, config)',
      )
      .eq('entity_id', clientId)
      .eq('organization_id', organizationId)
      .order('entered_at', { ascending: false })
      .limit(5);
    return (data ?? []) as unknown as AutomazioneSulLead[];
  }, [clientId, organizationId]);

  useEffect(() => {
    if (!clientId || !organizationId) return;
    let annullato = false;
    void carica().then((r) => {
      if (annullato) return;
      setRighe(r);
      setCaricato(true);
    });
    return () => {
      annullato = true;
    };
  }, [clientId, organizationId, carica]);

  /**
   * "Ferma automazione": il lead esce dal flusso qui e ora, con il motivo scritto
   * nella scheda. Deciso nella riunione commerciale del 21/09/2026: chi prende in
   * mano un lead deve poter spegnere Clara e le sequenze senza passare dal costruttore.
   */
  const ferma = async (a: AutomazioneSulLead) => {
    const nomeFlusso = a.workflow?.name || 'questa automazione';
    const ok = await confirm({
      title: 'Fermare l\'automazione?',
      message: `"${nomeFlusso}" si ferma per questo lead: niente piu' messaggi automatici, attese o risposte dell'assistente. Per riattivarla dovrai far rientrare il lead nel flusso.`,
      confirmLabel: 'Ferma',
      cancelLabel: 'Annulla',
      type: 'danger',
    });
    if (!ok) return;
    setFermando(a.id);
    const chi = userProfile?.full_name || userProfile?.email || 'un utente';
    const { error } = await supabase
      .from('automation_workflow_entities')
      .update({
        status: 'exited',
        exited_at: new Date().toISOString(),
        exit_reason: `Fermata dalla scheda da ${chi}`,
        next_action_at: null,
        current_step_id: null,
      })
      .eq('id', a.id)
      .eq('organization_id', organizationId);
    setFermando(null);
    if (error) {
      toast.error("Non sono riuscito a fermare l'automazione");
      return;
    }
    toast.success('Automazione fermata');
    setRighe(await carica());
  };

  if (!caricato || righe.length === 0) return null;

  const attive = righe.filter((r) => r.status === 'active' || r.status === 'waiting' || r.status === 'pending');
  const ultimeConcluse = righe.filter((r) => !attive.includes(r)).slice(0, 2);
  const daMostrare = [...attive, ...ultimeConcluse];

  return (
    <div className="mb-4 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-[#454B4E] px-3 py-2.5">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#9B9A97] dark:text-gray-500">
        <Workflow className="h-3.5 w-3.5" />
        Automazioni
      </div>
      <div className="space-y-2">
        {daMostrare.map((a) => {
          const r = descriviAutomazione(a);
          const { Icon, colore } = ICONA[r.tono];
          return (
            <div key={a.id} className="flex items-start gap-2">
              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${colore}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-[#37352F] dark:text-white">{r.titolo}</div>
                {r.dettaglio && <div className="text-[11px] text-[#9B9A97] dark:text-gray-500">{r.dettaglio}</div>}
              </div>
              {attive.includes(a) && (
                <button
                  type="button"
                  onClick={() => void ferma(a)}
                  disabled={fermando === a.id}
                  title="Ferma l'automazione per questo lead"
                  className="flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] hover:text-[#EB5757] disabled:opacity-50 dark:text-gray-400 dark:hover:bg-white/[0.08]"
                >
                  <Square className="h-3 w-3" strokeWidth={1.5} />
                  {fermando === a.id ? 'Fermo...' : 'Ferma'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
